import { PrimaryButton } from '@/components/button/button-component';
import { Col, Row } from '@/components/grid/grid-component';
import Modal from '@/components/modal/modal-component';
import FieldWithRevert from '@/components/react-hook-form-inputs/field-with-revert/field-with-revert-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import useSelectStyle from '@/hooks/use-select-style';
import { recursiveFlatMap } from '@/pages/company-management/company-products/utils';
import { BodyText } from '@/utils/global-fonts';
import { getNestedErrorMessage } from '@/utils/utils';
import { requiredValidationMessage } from '@/utils/validations';
import FocusRing from '@atlaskit/focus-ring';
import {
  attachInstruction,
  extractInstruction,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/list-item';
import { GroupDropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/group';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/list-item';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import PropTypes from 'prop-types';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { CiFolderOn } from 'react-icons/ci';
import { IoMdArrowDropdown, IoMdArrowDropright } from 'react-icons/io';
import { MdOutlineDragIndicator, MdOutlinePlaylistAdd } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import invariant from 'tiny-invariant';
import { v4 } from 'uuid';
import OptionRowForm from './option-row-form';
import ProductOptionModal from './product-option-modal';
import ProductOptionVariantModal from './product-option-variant-modal';
import { styledOption as Styled } from './product-options-style';
import { getSelectedOption, handleAddNewVariant } from './products-utils';
import { useFieldArrayRegistry } from './context-provider';

/**
 * RenderPartNumber component to render the part number of the option.
 * @param {object} props - The props object containing options, name, item, and handleRevert.
 * @param {Array} props.options - The options array.
 * @param {string} props.name - The name of the option.
 * @param {object} props.item - The item to display.
 * @param {Function} props.handleRevert - The handle revert function to use.
 * @returns {JSX.Element} The rendered RenderPartNumber component.
 */
const RenderPartNumber = ({ options, name, item, handleRevert }) => {
  const { control } = useFormContext();
  const selectedOption = useWatch({
    control,
    name,
  });
  return (
    <BodyText $width="25%" color="inherit">
      {getSelectedOption(options, selectedOption)?.part_number}
      {item.template_to_option_link_id && (
        <FieldWithRevert
          fieldName={`productOptions.${item.template_to_option_link_id}.option_price_link_id`}
          tooltipMessage="Revert Part Number"
          inlineRevert
          handleRevert={(fieldName) =>
            handleRevert(fieldName, true, {
              formName: name,
              templateOptionId: item.template_to_option_link_id,
              field: 'option_price_link_id',
            })
          }
        />
      )}
    </BodyText>
  );
};

RenderPartNumber.propTypes = {
  options: PropTypes.array.isRequired,
  name: PropTypes.string.isRequired,
  item: PropTypes.object.isRequired,
  handleRevert: PropTypes.func.isRequired,
};

const Preview = () => <div>Item</div>;

function delay({ waitMs, fn }) {
  let timeoutId = window.setTimeout(() => {
    timeoutId = null;
    fn();
  }, waitMs);
  return function cancel() {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
}

/**
 * Folder component for displaying a folder structure.
 * @param {object} props - The props object containing name and children.
 * @param {string} props.name - The name of the folder.
 * @param {ReactNode} props.children - The content inside the folder.
 * @param {Function} props.action - The action handler.
 * @param {number} props.indent - The indentation level.
 * @param {object} props.siblings - The siblings array.
 * @param {object} props.CONTEXT - The context to use.
 * @param {Function} props.handleRevert - The handle revert function to use.
 * @param {object} props.item - The item to display.
 * @param {number} props.index - The index of the item.
 * @param {Function} props.replace - The replace handler.
 * @param {object} props.folderOpenRef - The reference to the folder open state.
 * @returns {JSX.Element} The rendered Folder component.
 */
const Folder = ({
  name,
  children,
  action,
  indent,
  item,
  siblings,
  index,
  replace,
  CONTEXT,
  handleRevert,
  folderOpenRef,
}) => {
  const folderRef = folderOpenRef;
  const [isOpen, setIsOpen] = useState(folderRef.current[name] ?? false);
  const { options, uniqueContextId } = useContext(CONTEXT);
  const [changeOrder, setChangeOrder] = useState(false);
  const [order, setOrder] = useState({ label: index + 1, value: index + 1 });
  const dragRef = useRef(null);
  const [instruction, setInstruction] = useState(null);

  const {
    getValues,
    formState: { errors },
  } = useFormContext();

  // Check if item is draggable (not linked to template and has siblings)
  const isDraggable = !item.template_to_option_link_id;
  const cancelExpandRef = useRef(null);

  const extraToppings = {
    marginTop: '12px',
    justifyContent: 'flex-end',
    display: 'flex',
  };
  const rowStyle = { width: 'auto' };
  const selectStyle = useSelectStyle();

  const selectOrderOptions = siblings.map((sibling, idx) => ({
    label: idx + 1,
    value: idx + 1,
    isDisabled: sibling.template_to_option_link_id,
  }));

  const handleChangeOrder = () => {
    const targetIndex = order.value - 1; // Convert to 0-based index
    replace(index, targetIndex);
    setChangeOrder(false);
  };

  const selectedOption = useMemo(
    () => getSelectedOption(options, item.option_price_link_id)?.part_number,
    [options, item],
  );

  const toggleFolder = () => {
    folderRef.current[name] = !folderRef.current[name];
    setIsOpen(!isOpen);
  };

  const cancelExpand = useCallback(() => {
    cancelExpandRef.current?.();
    cancelExpandRef.current = null;
  }, []);

  useEffect(() => {
    function onChange({ self }) {
      const currentInstruction = extractInstruction(self.data);

      // expand after 500ms if still merging
      if (
        currentInstruction?.operation === 'combine' &&
        item.children.length &&
        !isOpen &&
        !cancelExpandRef.current
      ) {
        cancelExpandRef.current = delay({
          waitMs: 1000,
          fn: () => {
            folderRef.current[name] = true;
            setIsOpen(true);
          },
        });
      }
      if (
        currentInstruction?.operation !== 'combine' &&
        cancelExpandRef.current
      ) {
        cancelExpand();
      }

      setInstruction(currentInstruction);
    }

    if (dragRef?.current) {
      invariant(dragRef.current);

      return combine(
        draggable({
          element: dragRef.current,
          getInitialData: () => ({
            id: item.id,
            type: 'tree-item',
            isOpenOnDragStart: false,
            name,
            dropName: `${name}.children`,
            index,
            isDraggable,
            uniqueContextId,
          }),
          canDrag: () => isDraggable,
          onGenerateDragPreview: ({ nativeSetDragImage }) => {
            setCustomNativeDragPreview({
              getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),
              render: ({ container }) => {
                // eslint-disable-next-line react/no-deprecated
                ReactDOM.render(<Preview item={item} />, container);
                // eslint-disable-next-line react/no-deprecated
                return () => ReactDOM.unmountComponentAtNode(container);
              },
              nativeSetDragImage,
            });
          },
        }),
        dropTargetForElements({
          element: dragRef.current,
          getData: ({ input, element }) => {
            const data = {
              id: item.id,
              name,
              index,
              isDraggable,
              dropName: `${name}.children`,
            };
            return attachInstruction(data, {
              input,
              element,
              operations: {
                combine: 'available',
                'reorder-before': 'available',
                // Don't allow 'reorder-after' on expanded items
                'reorder-after':
                  isOpen && item.children.length
                    ? 'not-available'
                    : 'available',
              },
            });
          },
          canDrag: () => isDraggable,
          canDrop: ({ source }) =>
            source.data.type === 'tree-item' &&
            source.data.id !== item.id &&
            source.data.uniqueContextId === uniqueContextId,
          onDragEnter: onChange,
          onDrag: onChange,
          onDragLeave: () => {
            cancelExpand();
            setInstruction(null);
          },
          onDrop: () => {
            cancelExpand();
            setInstruction(null);
          },
        }),
      );
    }
  }, [
    item,
    uniqueContextId,
    isOpen,
    folderRef,
    name,
    cancelExpand,
    index,
    isDraggable,
  ]);

  return (
    <Styled.Folder>
      <Styled.FolderHeader>
        <FocusRing isInset>
          <Styled.ItemLabel
            ref={dragRef}
            className={`folder-name ${isOpen ? 'open' : ''}`}
            type="button"
            tabIndex={indent}
            indent={indent}
            $color={
              getNestedErrorMessage(`${name}.name`, errors) ===
              'Invalid Connection'
                ? 'red'
                : null
            }
            style={{
              cursor: isDraggable ? 'grab' : 'default',
            }}
          >
            <Styled.Span>
              {siblings.length > 1 ? (
                <Styled.ItemIconAndTypeWrapper>
                  <MdOutlineDragIndicator
                    onClick={(e) => {
                      e.stopPropagation();
                      // Keep modal as fallback option for manual reorder
                      if (!item.template_to_option_link_id) {
                        setChangeOrder(true);
                      }
                    }}
                    className={`icon ${isDraggable ? 'click' : ''}`}
                    color="inherit"
                    style={{
                      cursor: isDraggable ? 'grab' : 'not-allowed',
                      opacity: isDraggable ? 1 : 0.5,
                    }}
                  />
                  <Modal
                    open={changeOrder}
                    close={() => setChangeOrder(false)}
                    showClose
                    width="300px"
                    title="Switch Position With:"
                    bodyStyle={{ display: 'block' }}
                  >
                    <Row style={rowStyle}>
                      <Col xs={12}>
                        <Select
                          options={selectOrderOptions}
                          onChange={(e) => setOrder(e)}
                          value={order}
                          menuPortalTarget={document.getElementById(
                            'select-option',
                          )}
                          styles={selectStyle}
                        />
                      </Col>
                      <Col xs={12} style={extraToppings}>
                        <PrimaryButton
                          onClick={handleChangeOrder}
                          title="Confirm"
                        />
                      </Col>
                    </Row>
                  </Modal>
                </Styled.ItemIconAndTypeWrapper>
              ) : (
                <Styled.ItemIconAndTypeWrapper>
                  <MdOutlineDragIndicator
                    className={`icon ${isDraggable ? 'click' : ''}`}
                    color="inherit"
                    style={{
                      cursor: isDraggable ? 'grab' : 'not-allowed',
                      opacity: isDraggable ? 1 : 0.5,
                    }}
                  />
                </Styled.ItemIconAndTypeWrapper>
              )}
              <Styled.ItemIconAndTypeWrapper onClick={toggleFolder}>
                {item.children.length > 0 &&
                  (isOpen ? (
                    <IoMdArrowDropdown className="icon" color="inherit" />
                  ) : (
                    <IoMdArrowDropright className="icon" color="inherit" />
                  ))}
                {item.option_type === 'folder' ? (
                  <CiFolderOn className="icon" color="inherit" />
                ) : (
                  <MdOutlinePlaylistAdd className="icon" color="inherit" />
                )}
              </Styled.ItemIconAndTypeWrapper>
              <Styled.ItemNameWrapper type={item.option_type}>
                <Input
                  noMargin
                  noExtras
                  shouldInheritBackgroundColor={item?.is_hidden}
                  placeholder="Please enter folder name"
                  inputStyle={{
                    border: 'none',
                    outline: 'none',
                    margin: '0',
                    width: item.option_type === 'folder' ? '100%' : '90%',
                    backgroundColor: item.is_hidden ? 'inherit' : 'initial',
                  }}
                  name={`${name}.name`}
                  registerOptions={{
                    required: requiredValidationMessage,
                    validate: () => {
                      const fieldToValidate = getValues(name);

                      const checkRecursive = (currentItem) =>
                        currentItem?.children?.some(
                          (child) =>
                            (child?.option_type === 'option' &&
                              !getSelectedOption(
                                options,
                                child?.option_price_link_id,
                              )) ||
                            checkRecursive(child),
                        );

                      const invalidKids = checkRecursive(fieldToValidate);

                      const invalid =
                        invalidKids ||
                        (item.option_type === 'option' && !selectedOption);

                      return !invalid || 'Invalid Connection';
                    },
                  }}
                />
              </Styled.ItemNameWrapper>
              {item.option_type === 'option' && (
                <RenderPartNumber
                  options={options}
                  name={`${name}.option_price_link_id`}
                  item={item}
                  handleRevert={handleRevert}
                />
              )}
            </Styled.Span>
            <OptionRowForm
              name={name}
              action={action}
              item={item}
              handleRevert={handleRevert}
            />
            {instruction ? <DropIndicator instruction={instruction} /> : null}
          </Styled.ItemLabel>
        </FocusRing>
      </Styled.FolderHeader>
      <div
        className="folder-content"
        style={
          isOpen
            ? { height: 'auto', overflow: 'auto' }
            : { height: 0, overflow: 'hidden' }
        }
      >
        {children}
      </div>
    </Styled.Folder>
  );
};

Folder.defaultProps = {
  name: 'Folder',
  children: null,
  indent: 1,
  index: 0,
  siblings: [],
  handleRevert: null,
  folderOpenRef: null,
};

Folder.propTypes = {
  name: PropTypes.string,
  action: PropTypes.func.isRequired,
  replace: PropTypes.func.isRequired,
  children: PropTypes.node,
  indent: PropTypes.number,
  index: PropTypes.number,
  siblings: PropTypes.arrayOf(
    PropTypes.shape({
      template_to_option_link_id: PropTypes.string,
    }),
  ),
  CONTEXT: PropTypes.object.isRequired,
  item: PropTypes.shape({
    id: PropTypes.string,
    children: PropTypes.array,
    order: PropTypes.number,
    option_type: PropTypes.string,
    option_price_link_id: PropTypes.string,
    is_hidden: PropTypes.bool,
    template_to_option_link_id: PropTypes.string,
  }).isRequired,
  handleRevert: PropTypes.func,
  folderOpenRef: PropTypes.object,
};

/**
 * Component to render a folder structure recursively.
 * @param {object} props - The props object containing data.
 * @param  {string} props.name - the parent name
 * @param {number} props.indent - How many times it has been indented.
 * @param {object} props.options - The field array.
 * @param {object} props.CONTEXT - The context to use.
 * @param {Function} props.handleRevert - The handle revert function to use.
 * @param {object} props.folderOpenRef - The reference to the folder open state.
 * @param {object} props.mainRef - The reference to the main element.
 * @returns {JSX.Element} The rendered folder structure.
 */
const OptionStructure = ({
  name,
  indent,
  options,
  CONTEXT,
  handleRevert,
  folderOpenRef,
  mainRef,
}) => {
  const { id: companyId } = useParams();
  const { control, getValues, trigger, setValue } = useFormContext();
  const [isOpenAddOption, setIsOpenAddOption] = useState(false);
  const [isOpenAddOptionVariant, setIsOpenAddOptionVariant] = useState(false);
  const [createVariantLoading, setCreateVariantLoading] = useState(false);
  const [groupState, setGroupState] = useState('idle');
  const { register, unregister } = useFieldArrayRegistry();
  const {
    options: optionsData,
    setOptions,
    templateData,
    uniqueContextId,
  } = useContext(CONTEXT);

  const groupRef = useRef(null);

  const faOptions = useFieldArray({
    control,
    name, // unique name for your Field Array
  });
  let fieldOptions = faOptions;
  if (options && indent === 1) {
    fieldOptions = options;
  }
  const { fields, update, remove, move, append } = fieldOptions;

  useEffect(() => {
    register(name, fieldOptions);
    return () => unregister(name);
  }, [name, fieldOptions, register, unregister]);

  const action = (type, index) => {
    const values = getValues(name);
    const fieldToUpdate = values[index];
    const kids = fieldToUpdate.children;

    const recursiveNewId = (value, parentId) => {
      const newValue = value;
      newValue.id = v4();
      newValue.parent_id = parentId;
      if (newValue.children) {
        newValue.children = newValue.children.map((child) =>
          recursiveNewId(child, newValue.id),
        );
      }
      return newValue;
    };

    switch (type) {
      case 'folder': {
        kids.push({
          option_type: 'folder',
          id: v4(),
          parent_id: fieldToUpdate.id,
          select_type: 'multi',
          is_required: false,
          children: [],
          name: '',
        });
        update(index, {
          ...fieldToUpdate,
          children: kids,
        });
        break;
      }
      case 'option': {
        setIsOpenAddOption({ name, index });
        break;
      }
      case 'editOption': {
        setIsOpenAddOption({ name, index, values: fieldToUpdate });
        break;
      }
      case 'duplicateOption': {
        const newField = JSON.parse(JSON.stringify(fieldToUpdate));
        append(recursiveNewId(newField, newField.parent_id));
        break;
      }
      case 'optionVariant': {
        setIsOpenAddOptionVariant({ name, index, values: fieldToUpdate });
        break;
      }
      case 'delete':
        remove(index);
        break;
      case 'show':
        update(index, {
          ...fieldToUpdate,
          is_hidden: false,
          children: fieldToUpdate.children.map((child) => ({
            ...child,
            is_hidden: false,
          })),
        });
        break;
      case 'hide':
        update(index, {
          ...fieldToUpdate,
          is_hidden: true,
          children: fieldToUpdate.children.map((child) => ({
            ...child,
            is_hidden: true,
          })),
        });
        break;
      default:
        break;
    }
  };

  const handleAddOption = (submitValues) => {
    const { name: fieldName, index, values: isUpdate } = isOpenAddOption;
    const values = getValues(fieldName);
    const fieldToUpdate = values[index];
    const kids = fieldToUpdate.children;
    const option = getSelectedOption(
      optionsData,
      submitValues.option_price_link_id,
    );
    if (isUpdate) {
      fieldToUpdate.dependencies = submitValues.dependencies;
      const shouldTrigger = !fieldToUpdate.option_price_link_id;
      fieldToUpdate.option_price_link_id = submitValues.option_price_link_id;
      if (fieldToUpdate.template_to_option_link_id) {
        if (templateData) {
          const templateOptions = recursiveFlatMap(templateData.productOptions);
          const templateOption = templateOptions.find(
            (opt) =>
              opt.template_to_option_link_id ===
              fieldToUpdate.template_to_option_link_id,
          );
          if (templateOption) {
            if (
              templateOption.option_price_link_id !==
              submitValues.option_price_link_id
            ) {
              setValue(
                `product.overrides.productOptions.${fieldToUpdate.template_to_option_link_id}.option_price_link_id`,
                true,
              );
            }
            const templateDependencies = templateOption.dependencies.every(
              (dep) => submitValues.dependencies.includes(dep),
            );
            if (
              !templateDependencies ||
              templateOption.dependencies.length !==
                submitValues.dependencies.length
            ) {
              setValue(
                `product.overrides.productOptions.${fieldToUpdate.template_to_option_link_id}.dependencies`,
                true,
              );
            }
          }
        }
      }
      update(index, {
        ...fieldToUpdate,
        name: option.name,
        option_price_link_id: submitValues.option_price_link_id,
        dependencies: submitValues.dependencies,
      });
      if (shouldTrigger) {
        trigger('productOptions');
      }
      setIsOpenAddOption(false);
    } else {
      kids.push({
        option_type: 'option',
        id: v4(),
        parent_id: fieldToUpdate.id,
        select_type: 'multi',
        is_required: false,
        children: [],
        name: option?.name,
        option_price_link_id: submitValues.option_price_link_id,
        dependencies: submitValues.dependencies,
      });
      update(index, {
        ...fieldToUpdate,
        children: kids,
      });
      setIsOpenAddOption(false);
    }
  };

  const addNewVariant = async (values) => {
    const optionId = isOpenAddOptionVariant?.values?.option_id;
    const productId = isOpenAddOptionVariant?.values?.product_id;

    await handleAddNewVariant({
      values,
      companyId,
      setOptions,
      setLoading: setCreateVariantLoading,
      setIsOpen: setIsOpenAddOptionVariant,
      optionId,
      productId,
      append,
      name,
    });
  };

  const getOptionItem = (item, index, siblings) => {
    if (!item) return null;

    return (
      <Folder
        index={index}
        parent={name}
        name={`${name}.${index}`}
        action={(type) => action(type, index)}
        indent={indent}
        item={item}
        siblings={siblings}
        replace={move}
        CONTEXT={CONTEXT}
        handleRevert={handleRevert}
        folderOpenRef={folderOpenRef}
      >
        {item.children.length > 0 && (
          <OptionStructure
            parentId={item.id}
            name={`${name}.${index}.children`}
            indent={indent + 1}
            CONTEXT={CONTEXT}
            handleRevert={handleRevert}
            folderOpenRef={folderOpenRef}
            mainRef={mainRef}
          />
        )}
      </Folder>
    );
  };

  useEffect(() => {
    const group = groupRef.current;
    // item has no children or is not open
    if (!group) {
      return;
    }

    function onChange({ location, self }) {
      const [innerMost] = location.current.dropTargets.filter(
        (dropTarget) => dropTarget.data.type === 'group',
      );

      setGroupState(
        innerMost?.element === self.element ? 'is-innermost-over' : 'idle',
      );
    }

    return dropTargetForElements({
      element: group,
      canDrop: ({ source }) =>
        source.data.type === 'tree-item' &&
        source.data.id !== name &&
        source.data.uniqueContextId === uniqueContextId,
      getData: () => ({ type: 'group' }),
      getIsSticky: () => false,
      onDragStart: onChange,
      onDropTargetChange: onChange,
      onDragLeave: () => setGroupState('idle'),
      onDrop: () => setGroupState('idle'),
    });
  }, [name, uniqueContextId]);

  return (
    <>
      <GroupDropIndicator
        isActive={groupState === 'is-innermost-over'}
        ref={groupRef}
      >
        {fields?.map((item, idx) => (
          <Styled.OptionCol key={item.id} $isHidden={item.is_hidden}>
            <Styled.OptionWrapper>
              {getOptionItem(item, idx, fields)}
            </Styled.OptionWrapper>
          </Styled.OptionCol>
        ))}
      </GroupDropIndicator>
      <ProductOptionModal
        isOpen={isOpenAddOption}
        setIsOpen={setIsOpenAddOption}
        onSubmit={handleAddOption}
        CONTEXT={CONTEXT}
        name={name}
      />
      <ProductOptionVariantModal
        isOpen={isOpenAddOptionVariant}
        setIsOpen={setIsOpenAddOptionVariant}
        onSubmit={addNewVariant}
        loading={createVariantLoading}
      />
    </>
  );
};

OptionStructure.defaultProps = {
  options: null,
  handleRevert: null,
  folderOpenRef: null,
  mainRef: null,
};

OptionStructure.propTypes = {
  name: PropTypes.string.isRequired,
  indent: PropTypes.number.isRequired,
  options: PropTypes.func,
  CONTEXT: PropTypes.object.isRequired,
  handleRevert: PropTypes.func,
  folderOpenRef: PropTypes.object,
  mainRef: PropTypes.object,
};

export default OptionStructure;
